import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  isRouteErrorResponse,
  Link,
} from "react-router";
import type { LinksFunction } from "react-router";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import packageJson from "../package.json";

import "./app.css";
import { RouteLoadingFallback } from "~/components/routeLoadingFallback";
import { AuthProvider } from "~/context/authContext";
import { CallProvider } from "~/context/callContext";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
];

const SENTRY_DSN =
  (import.meta.env.VITE_SENTRY_DSN as string | undefined) ??
  "https://19bd09b2534ed79e26bca7181670c068@sentry.openvantage.io/25";
const APP_VERSION = `v${packageJson.version}`;
const DEFAULT_SENTRY_RELEASE = `${packageJson.name}@${packageJson.version}`;
const SENTRY_RELEASE =
  (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ??
  DEFAULT_SENTRY_RELEASE;
const rawEnvironment =
  (import.meta.env.VITE_ENVIRONMENT as string | undefined) ??
  (import.meta.env.MODE as string | undefined) ??
  "unknown";
const normalizedEnvironment = rawEnvironment.trim().toLowerCase();
const SENTRY_ENVIRONMENT =
  normalizedEnvironment === "development" || normalizedEnvironment === "local"
    ? "dev"
    : normalizedEnvironment;
const IS_LOCAL_DEVELOPMENT =
  import.meta.env.DEV ||
  normalizedEnvironment === "development" ||
  normalizedEnvironment === "local" ||
  normalizedEnvironment === "localhost";
const SENTRY_ENABLED = Boolean(SENTRY_DSN) && !IS_LOCAL_DEVELOPMENT;

// Initialize as early as possible so errors during route rendering are captured.
if (typeof window !== "undefined" && SENTRY_ENABLED) {
  const tracePropagationTargets: Array<string | RegExp> = ["localhost"];
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (apiUrl) {
    try {
      tracePropagationTargets.push(new URL(apiUrl).origin);
    } catch {
      // Ignore invalid URL values.
    }
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    release: SENTRY_RELEASE,
    environment: SENTRY_ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    tracePropagationTargets,
    replaysSessionSampleRate: 0.1, // 10%
    replaysOnErrorSampleRate: 1.0, // 100% when sampling errors
    initialScope: {
      tags: {
        app_version: APP_VERSION,
      },
    },
  });
}

export function Layout({ children }: { children: React.ReactNode }) {

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const HydrateFallback = () => (
  <RouteLoadingFallback
    title="Loading AidCall"
    description="Loading modules and preparing your workspace…"
  />
);

export default function App() {
  return (
    <>
      <AuthProvider>
        <CallProvider>
          <Outlet />
        </CallProvider>
      </AuthProvider>
      <div
        className="fixed bottom-5 right-[-40px] z-50 select-none pointer-events-none"
        aria-hidden
      >
        <div
          className="bg-amber-400 w-28 text-amber-950 text-[9px] font-semibold py-0.5 shadow-sm"
          style={{ transform: "rotate(320deg)" }}
        >
          <p className="text-center">{APP_VERSION}</p>
        </div>
      </div>
    </>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  let title = "Something went wrong";
  let description = "An unexpected error occurred while loading this page.";
  let details: string | undefined;

  if (isRouteErrorResponse(error)) {
    title = `${error.status} ${error.statusText}`;
    description =
      error.data && typeof error.data === "string"
        ? error.data
        : "The server returned an error response.";
  } else if (error instanceof Error) {
    details = error.message;
  }

  const isDev = process.env.NODE_ENV !== "production";

  // React Router's ErrorBoundary catches rendering/loading errors instead
  // of crashing the app. Capture explicitly so they show up in Sentry.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!SENTRY_ENABLED) return;

    try {
      Sentry.captureException(error);
    } catch {
      // Never break the error UI if Sentry reporting fails.
    }
  }, [error]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <div className="flex min-h-screen flex-col items-center justify-center px-4">
        <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/50 backdrop-blur">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500/10 text-red-400">
              <span className="text-lg">!</span>
            </div>
            <div>
              <h1 className="text-lg font-semibold leading-tight">{title}</h1>
              <p className="text-xs text-slate-400">aidcall admin portal</p>
            </div>
          </div>

          <p className="mb-4 text-sm text-slate-300">{description}</p>

          {details && (
            <p className="mb-4 rounded-md bg-slate-900 px-3 py-2 text-xs text-slate-300">
              {details}
            </p>
          )}

          {isDev && Boolean(error) && !isRouteErrorResponse(error) && (
            <details className="mb-4 rounded-md bg-slate-900 px-3 py-2 text-xs text-slate-400">
              <summary className="cursor-pointer text-slate-300">
                Technical details (only visible in development)
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[11px] leading-snug">
                {error instanceof Error
                  ? error.stack ?? error.message
                  : JSON.stringify(error, null, 2)}
              </pre>
            </details>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.location.reload();
                }
              }}
              className="inline-flex items-center justify-center rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-900 shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            >
              Reload page
            </button>

            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-md border border-slate-500/60 px-3 py-1.5 text-xs font-medium text-slate-100 hover:border-slate-300/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            >
              Go to dashboard
            </Link>
          </div>

          <p className="mt-4 text-[10px] text-slate-500">
            If this keeps happening, please contact your system administrator or
            support team and mention that you encountered an unexpected error in
            the aidcall admin portal.
          </p>
        </div>

        <div
          className="mt-6 select-none text-[10px] font-medium text-slate-500"
          aria-hidden
        >
          {APP_VERSION}
        </div>
      </div>
    </div>
  );
}
